<template>
  <div
    class="top-header-fixed"
    :class="{
      'hide-accesscode': isHideTextAccessCode,
      'is-content-slide': isContentSlide,
    }"
  >
    <div>
      <div
        class="top-header horizontal layout center center-justified"
        :class="{'white':isTextColourLight,
                 'slideUp': isHideInstructionBar, 'slideDown': !isHideInstructionBar}"
        ref="top-header"
        @click="handleToggleQrCode()"
      >
        <div class="flex flex-row items-center justify-center text-center">
          <span>{{ $t("goTo") }}</span>
          <strong v-tooltip="audienceLinkTooltip" class="mr-2">
            <span
              @click="onClickAudienceLink"
              class="top-header-acode"
            >
              {{ getAudienceDomain }}/{{ presentation.accessCode }}
              <i class="fas fa-pencil-alt" />
            </span>
          </strong>
          <div
            class="inline-block w-6 h-6 icon-wrapper"
            v-tooltip="qrCodeTooltip"
            v-show="isPresenting"
          >
            <aha-icon
              name="qr-code2"
              class="w-full h-full -mt-0.5"
              v-if="!isQRCodeVisible"
            />
            <i
              class="fas fa-times icon-fix-w"
              v-if="isQRCodeVisible"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import configs from '@/config'
import constants from '@/constant'
import PresentationApi from '@/api/presentation'
import util from '@/util'
import ToggleQRCodeMixin from '@/mixins/presentation/ToggleQRCodeMixin'
import msTeamsUtils from '@/utils/ms-teams'

let domainAudienceTopCanvas = 'ahaslides.com'
if (configs.region === constants.euRegion) {
  domainAudienceTopCanvas = 'ahaslides.eu'
}

export default {
  mixins: [ToggleQRCodeMixin],
  data() {
    return {
      isMSTeamsApp: window.location.href.includes('ms-teams'),
      domainAudienceTopCanvas,
      hasCopiedAudienceLink: false,
    }
  },
  computed: {
    ...mapGetters(['isPresenting', 'isBackstage']),
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      isScreenCapture: (state) => state.isScreenCapture,
      sharePresentation: (state) => state.sharePresentation,
      isQRCodeVisible: (state) => state.isQRCodeVisible,
      audienceLink: (state) => {
        if (!state.presentation.whiteLabelLink) {
          return `https://${configs.audienceLink}/${state.presentation.uniqueAccessCode}`
        }
        return `https://${state.presentation.whiteLabelLink}/${state.presentation.uniqueAccessCode}`
      },
    }),
    isTextColourLight() {
      return this.slideActive.textColour === '#ffffff'
    },
    isPresentingOrBackstage() {
      return this.isPresenting || this.isBackstage
    },
    audienceLinkTooltip() {
      if (this.isPresentingOrBackstage) {
        const tooltipContent = this.hasCopiedAudienceLink ? 'Copied' : 'Click to copy link'
        return {
          content: tooltipContent,
          delay: { show: 0, hide: this.hasCopiedAudienceLink ? 600 : 10 },
          offset: -5,

        }
      }
      return null
    },
    getAudienceDomain() {
      // eslint-disable-next-line max-len
      if (this.$store.state.currentUser.whiteLabelLink) return this.$store.state.currentUser.whiteLabelLink
      return domainAudienceTopCanvas
    },
    isDocumentOrGoogleSlidesType() {
      const { type } = this.slideActive
      return type === 'document' || type === 'googleSlides' || type === 'freestyle'
    },
    isHideInstructionBar() {
      if (this.isScreenCapture) {
        return true
      }
      const { hideInstructionBar, hideIntroBarDocument } = this.presentation
      const isOtherSlideHideBar = !this.isDocumentOrGoogleSlidesType
        && hideInstructionBar
      const isDocumentGoogleSlideHideBar = this.isDocumentOrGoogleSlidesType
        && hideIntroBarDocument

      return isOtherSlideHideBar || isDocumentGoogleSlideHideBar
    },
    hasNoPresentationCode() {
      const { qrcode, shareCode, uniqueAccessCode } = this.presentation
      return !!qrcode || !shareCode || !uniqueAccessCode
    },
    isHideTextAccessCode() {
      const { sharePresentation } = this.$store.state
      return !!util.getParamValue('hideAccessCode', window.location.href) && sharePresentation
    },
    isContentSlide() {
      return this.slideActive && this.slideActive.type === 'freestyle'
    },
    qrCodeTooltip() {
      return {
        content: !this.isQRCodeVisible ? 'Show QR code' : 'Hide QR code',
        offset: 15,
      }
    },
  },
  methods: {
    ...mapActions(['patchPresentationAction']),
    handleToggleQrCode() {
      try {
        if (!this.isPresenting) return
        this.toggleQrCode('Instruction bar') // ToggleQRCodeMixin
        if (this.hasNoPresentationCode && !this.sharePresentation) {
          const res = PresentationApi.getPresentationBasic(this.presentation.id)
          this.presentation.qrcode = res.body.qrcode
          this.presentation.shareCode = res.body.shareCode
          this.presentation.uniqueAccessCode = res.body.uniqueAccessCode
        }
      } catch (error) {
        //
      }
    },

    onClickAudienceLink(e) {
      e.stopPropagation()
      if (this.isPresentingOrBackstage) {
        if (this.isMSTeamsApp) {
          msTeamsUtils.copyTextWhenAppIsMSTeams(e.target)
        } else {
          navigator.clipboard.writeText(`https://${this.presentation.whiteLabelLink ||
                this.domainAudienceTopCanvas}/${this.presentation.accessCode}`).catch(console.log)
        }

        this.hasCopiedAudienceLink = true
        setTimeout(() => {
          this.hasCopiedAudienceLink = false
        }, 1500)
        util.gaTrackEvent(constants.gaCategories.PRESENTATION_EDITOR, 'Presenting > Copy audience link')
      } else {
        this._openConfigure(e)
        util.gaTrackEvent(constants.gaCategories.PRESENTATION_EDITOR, 'Instruction bar > Edit access code')
      }
    },

    _openConfigure(e) {
      if (this.isPresenting || this.isBackstage) return
      e.stopPropagation()
      this.presentation.showShare = !this.presentation.showShare
      if (this.presentation.showShare) {
        document.body.classList.add('no-scroll')
      } else {
        document.body.classList.remove('no-scroll')
      }
      this.patchPresentationAction({
        showShare: this.presentation.showShare,
      })
      setTimeout(() => {
        const blockChangeAcode = document.getElementById('block-content-change-acode')
        const inputChangeAcode = document.getElementById('setting-change-access-code-input')
        if (blockChangeAcode) {
          blockChangeAcode.classList.add('input-focus')
        }
        if (inputChangeAcode) {
          inputChangeAcode.focus()
          this.addClassActiveInputAccessCode(inputChangeAcode)
        }
      }, 1)
    },

    addClassActiveInputAccessCode(eleInputChangeAcode) {
      const eleInputChangeAccessCode = eleInputChangeAcode.closest('.change-access-code-input-wrp')
      if (eleInputChangeAccessCode) {
        eleInputChangeAccessCode.classList.add('access-code-input--active')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.top-header-fixed {
  width: 100%;
  z-index: 125;
  height: 58px;
  &.is-content-slide {
    height: unset;
  }
}
.top-header {
  -webkit-user-select: text; /* Chrome 49+ */
  -moz-user-select: text; /* Firefox 43+ */
  -ms-user-select: text; /* No support yet */
  user-select: text;
  height: 58px;
  background-color: rgba(240, 240, 240, 0.6);
  cursor: pointer;
  font-size: 27px;
  z-index: 121;

  &.slideDown {
    margin-top: unset;
    position: unset;
    transition: all 0.35s ease;
  -webkit-transition: all 0.35s ease;
  }

  &.slideUp {
    margin-top: -58px;
    position: relative;
    transition: all 0.35s ease;
  -webkit-transition: all 0.35s ease;

    @media (max-width: 768px) and (orientation: landscape) {
      z-index: -1;
    }

    &::after {
      top: 100%;
      content: '';
      width: 100%;
      height: 20px;
      position: absolute;
    }

    &:hover {
      margin-top: 0;
    }
  }
  .fa-info-circle {
    opacity: 0;
    font-size: 22px;
  }
  .fa-times {
    cursor: pointer;
    opacity: 0.85;
  }

  .img-logo-white {
    display: none;
  }

  &.white {
    background-color: rgba(255, 255, 255, .1) !important;
  }

  &:hover {
    .fa-info-circle {
      opacity: 0.85;
      transition: opacity 0.2s;
    }
  }

  strong {
    letter-spacing: 2px;
  }
  .top-header-acode {
    position: relative;
    // left: -8px;
    cursor: pointer;
    .fas.fa-pencil-alt {
      opacity: 0;
      font-size: 23px;
      margin-left: -3px;
    }
    &:hover {
      .fas.fa-pencil-alt {
        opacity: 1;
        -webkit-transition: opacity .2s linear;
        -moz-transition: opacity .2s linear;
        -ms-transition: opacity .2s linear;
        -o-transition: opacity .2s linear;
        transition: opacity .2s linear;
      }
    }
  }
}
.hide-accesscode {
  visibility: hidden;
}
</style>
